/**
 * Centralized authentication cleanup utilities
 * Provides reusable functions for clearing auth-related data from localStorage and sessionStorage
 */

/**
 * Clear all Amplify Cognito Identity keys from storage
 */
export function clearAmplifyIdentityKeys(): void {
  const identityPoolId = process.env.VUE_APP_COGNITO_IDENTITY_POOL_ID
  const region = process.env.VUE_APP_AWS_REGION || 'eu-west-1'

  const amplifyIdentityKeys = [
    `com.amplify.Cognito.${region}:${identityPoolId}.identityId`,
    `aws.cognito.identity-id.${region}:${identityPoolId}`,
    `aws.cognito.identity-credentials.${region}:${identityPoolId}`,
    `CognitoIdentityId-${region}:${identityPoolId}`,
    `CognitoIdentityServiceProvider.${identityPoolId}.LastAuthUser`,
  ]

  amplifyIdentityKeys.forEach(key => {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  })
}

/**
 * Clear all OIDC session data from localStorage
 */
export function clearOIDCSessionData(): void {
  const oidcKeys = Object.keys(localStorage).filter(key => key.startsWith('oidc.') || key.includes('oidc-client'))
  oidcKeys.forEach(key => localStorage.removeItem(key))
}

/**
 * Clear all Amplify Auth data from localStorage
 */
export function clearAmplifyAuthData(): void {
  const amplifyKeys = Object.keys(localStorage).filter(
    key =>
      key.startsWith('com.amplify.') ||
      key.startsWith('aws.') ||
      key.includes('CognitoIdentity') ||
      key.includes('CognitoUser'),
  )
  amplifyKeys.forEach(key => localStorage.removeItem(key))
}

/**
 * Extract identity ID from error message
 * @param errorMessage - Error message containing identity ID
 * @returns Identity ID if found, null otherwise
 */
export function extractIdentityIdFromError(errorMessage: string): string | null {
  // Match any AWS region format (e.g., eu-west-1, us-east-1, ap-southeast-2)
  const identityMatch = errorMessage.match(/[a-z]{2}-[a-z]+-\d+:[\w-]+/)
  return identityMatch ? identityMatch[0] : null
}

/**
 * Clear specific identity key from localStorage
 * @param identityId - The identity ID to clear (e.g., "eu-west-1:8637caea-bbcd-4fbd-ab52-1cb5f12f0c09")
 */
export function clearSpecificIdentity(identityId: string): void {
  const key = `com.amplify.Cognito.${identityId}.identityId`
  localStorage.removeItem(key)
}

/**
 * Comprehensive cleanup of all authentication-related data
 * Use this for complete sign out or error recovery
 */
export function clearAllAuthData(): void {
  // Clear Amplify Cognito Identity
  clearAmplifyIdentityKeys()

  // Clear OIDC session data
  clearOIDCSessionData()

  // Clear Amplify Auth data
  clearAmplifyAuthData()

  // Clear session storage
  sessionStorage.clear()
}

/**
 * Check if an error is an "Identity forbidden" error
 * @param error - Error object to check
 * @returns True if this is an identity forbidden error
 */
export function isIdentityForbiddenError(error: any): boolean {
  return error?.message?.includes('Access to Identity') && error?.message?.includes('is forbidden')
}
